/**
 * DayTeamLeaderSelector - Sélecteur de chef d'équipe par jour
 * Permet de définir un chef d'équipe différent pour un jour spécifique
 */
import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const DayTeamLeaderSelector = ({
  day,
  prestationTeamLeaderId,
  currentOverride,
  teamLeaders = [],
  dayParticipants = [],
  token,
  sessionId,
  onUpdate,
  onParticipantsChange,
  showSuccess = () => {},
  showError = () => {},
  showWarning = () => {}
}) => {
  const [saving, setSaving] = useState(false);

  // Chef d'équipe effectif : override ou défaut prestation
  const effectiveTeamLeaderId = currentOverride || prestationTeamLeaderId;
  const hasOverride = currentOverride !== null && currentOverride !== undefined && currentOverride !== prestationTeamLeaderId;

  // Nom du chef par défaut pour l'affichage
  const defaultLeader = teamLeaders.find(l => l.id === prestationTeamLeaderId);
  const defaultLeaderName = defaultLeader
    ? `${defaultLeader.first_name} ${defaultLeader.last_name}`
    : 'Non défini';

  // Handler changement chef d'équipe - SELECT NATIF
  const handleSelectChange = async (e) => {
    const newTeamLeaderId = e.target.value || null;
    const oldTeamLeaderId = effectiveTeamLeaderId;

    if (newTeamLeaderId === oldTeamLeaderId) return;

    // Revenir au chef par défaut
    if (newTeamLeaderId === prestationTeamLeaderId || !newTeamLeaderId) {
      await clearOverride(oldTeamLeaderId);
      return;
    }

    // Mode serveur
    if (sessionId && day.id) {
      setSaving(true);
      try {
        const response = await fetch(API_ENDPOINTS.PRESTATION_DAY_TEAM_LEADER(day.id), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ team_leader_id: newTeamLeaderId })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error);

        onUpdate(day.date, newTeamLeaderId);
        await swapParticipants(oldTeamLeaderId, newTeamLeaderId);
        showSuccess('Chef d\'équipe modifié');
      } catch (err) {
        showError(err.message || 'Erreur');
      } finally {
        setSaving(false);
      }
    } else {
      // Mode local
      onUpdate(day.date, newTeamLeaderId);
      await swapParticipants(oldTeamLeaderId, newTeamLeaderId);
      showSuccess('Chef d\'équipe modifié');
    }
  };

  // Effacer l'override
  const clearOverride = async (oldTeamLeaderId) => {
    if (sessionId && day.id) {
      setSaving(true);
      try {
        const response = await fetch(API_ENDPOINTS.PRESTATION_DAY_TEAM_LEADER(day.id), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ team_leader_id: null })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error);

        onUpdate(day.date, null);
        if (oldTeamLeaderId !== prestationTeamLeaderId) {
          await swapParticipants(oldTeamLeaderId, prestationTeamLeaderId);
        }
        showSuccess('Chef d\'équipe par défaut rétabli');
      } catch (err) {
        showError(err.message || 'Erreur');
      } finally {
        setSaving(false);
      }
    } else {
      onUpdate(day.date, null);
      if (oldTeamLeaderId !== prestationTeamLeaderId) {
        await swapParticipants(oldTeamLeaderId, prestationTeamLeaderId);
      }
      showSuccess('Chef d\'équipe par défaut rétabli');
    }
  };

  // Swap participants - avec sauvegarde API
  const swapParticipants = async (oldId, newId) => {
    if (!onParticipantsChange) return;

    // Normaliser les IDs en strings pour comparaison
    const oldIdStr = oldId ? String(oldId) : null;
    const newIdStr = newId ? String(newId) : null;
    let updated = dayParticipants.map(id => String(id));
    let changes = [];

    // Retirer l'ancien chef des participants
    if (oldIdStr) {
      if (updated.includes(oldIdStr)) {
        updated = updated.filter(id => id !== oldIdStr);
      }

      const old = teamLeaders.find(l => String(l.id) === oldIdStr);
      changes.push(`${old?.first_name || 'Ancien'} retiré`);

      // TOUJOURS appeler l'API pour supprimer
      if (sessionId && day.id) {
        try {
          await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(day.id), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action: 'remove_participant', user_id: oldIdStr })
          });
        } catch (err) {
          console.error('Erreur suppression participant:', err);
        }
      }
    }

    // Ajouter le nouveau chef aux participants
    if (newIdStr && !updated.includes(newIdStr)) {
      updated.push(newIdStr);
      const newL = teamLeaders.find(l => String(l.id) === newIdStr);
      changes.push(`${newL?.first_name || 'Nouveau'} ajouté`);

      // Sauvegarder en BDD si mode serveur
      if (sessionId && day.id) {
        try {
          await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(day.id), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action: 'add_participant', user_id: newIdStr })
          });
        } catch (err) {
          console.error('Erreur ajout participant:', err);
        }
      }
    }

    if (changes.length > 0) {
      onParticipantsChange(day.date, updated);
      showWarning(`Participants: ${changes.join(', ')}`, { duration: 3000 });
    }
  };

  return (
    <div style={{
      padding: '12px 16px',
      background: hasOverride ? '#fff3cd' : '#f8f9fa',
      borderRadius: '8px',
      marginBottom: '12px',
      border: hasOverride ? '2px solid #ffc107' : '1px solid #e9ecef'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{
          fontWeight: '600',
          fontSize: '13px',
          color: '#495057',
          minWidth: '140px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{ fontSize: '16px' }}>👷</span>
          Chef d'équipe ce jour :
        </label>

        <select
          value={effectiveTeamLeaderId || ''}
          onChange={handleSelectChange}
          disabled={saving}
          style={{
            flex: 1,
            minWidth: '200px',
            maxWidth: '300px',
            padding: '10px 12px',
            fontSize: '14px',
            border: '2px solid #e1e5e9',
            borderRadius: '10px',
            background: 'white',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          <option value="">-- Sélectionner --</option>
          {teamLeaders.map(leader => (
            <option key={leader.id} value={leader.id}>
              {leader.first_name} {leader.last_name}
              {leader.id === prestationTeamLeaderId ? ' (défaut)' : ''}
            </option>
          ))}
        </select>

        {hasOverride && (
          <button
            type="button"
            onClick={() => clearOverride(currentOverride)}
            disabled={saving}
            title={`Revenir à ${defaultLeaderName}`}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            ↺ Par défaut
          </button>
        )}

        {saving && <span style={{ fontSize: '12px', color: '#6c757d' }}>Enregistrement...</span>}
      </div>

      {hasOverride && (
        <div style={{ fontSize: '12px', color: '#856404', marginTop: '8px' }}>
          ⚠️ Chef modifié pour ce jour uniquement (défaut : {defaultLeaderName})
        </div>
      )}
    </div>
  );
};

export default DayTeamLeaderSelector;
